import React, {useEffect, useState, useCallback} from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  message,
  Row,
  Col,
  Tabs,
  Tag,
} from 'antd';
import {
  AndroidOutlined,
  AppleOutlined,
  SaveOutlined,
  ReloadOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import Api from 'configs';

interface PlatformCardProps {
  app: 'client' | 'partner';
  platform: 'android' | 'ios';
  form: any;
  title: string;
  icon: React.ReactNode;
  isSaving: boolean;
  onSave: (
    app: 'client' | 'partner',
    platform: 'android' | 'ios',
    form: any,
  ) => Promise<void>;
}

const PlatformCard: React.FC<PlatformCardProps> = ({
  app,
  platform,
  form,
  title,
  icon,
  isSaving,
  onSave,
}) => {
  const isForce = Form.useWatch('forceUpdate', form);

  return (
    <Card
      className="shadow-sm border rounded-xl h-full flex flex-col justify-between"
      title={
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 font-bold text-base">
            {icon} {title}
          </span>
          <Tag color={isForce ? 'red' : 'orange'}>
            {isForce ? 'Bắt buộc cập nhật (Force)' : 'Không bắt buộc'}
          </Tag>
        </div>
      }>
      <Form form={form} layout="vertical" initialValues={{forceUpdate: true}}>
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              label="Version tối thiểu (Chặn nếu <)"
              name="minVersion"
              tooltip="App có version thấp hơn giá trị này sẽ bị chặn và yêu cầu cập nhật"
              rules={[{required: true, message: 'Nhập version tối thiểu'}]}>
              <Input placeholder="Ví dụ: 2.1.2" />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              label="Build tối thiểu (Chặn nếu <)"
              name="minBuild"
              tooltip="Mã build nhỏ hơn số này sẽ bị chặn"
              rules={[{required: true, message: 'Nhập build tối thiểu'}]}>
              <InputNumber className="w-full" min={1} placeholder="Ví dụ: 15" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              label="Version mới nhất (Store)"
              name="latestVersion"
              rules={[{required: true, message: 'Nhập version mới nhất'}]}>
              <Input placeholder="Ví dụ: 2.1.2" />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              label="Build mới nhất (Store)"
              name="latestBuild"
              rules={[{required: true, message: 'Nhập build mới nhất'}]}>
              <InputNumber className="w-full" min={1} placeholder="Ví dụ: 15" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Chế độ Force Update (Bắt buộc cập nhật)"
          name="forceUpdate"
          valuePropName="checked"
          tooltip="BẬT: Popup không thể đóng, bắt buộc cập nhật mới dùng được app. TẮT: Popup cho phép bấm 'Để sau'.">
          <Switch checkedChildren="BẬT (Khóa app)" unCheckedChildren="TẮT (Nhắc nhở)" />
        </Form.Item>

        <Form.Item
          label="Tiêu đề popup thông báo"
          name="title"
          rules={[{required: true, message: 'Nhập tiêu đề'}]}>
          <Input placeholder="Ví dụ: Cập nhật phiên bản mới" />
        </Form.Item>

        <Form.Item
          label="Nội dung thông báo cập nhật"
          name="message"
          rules={[{required: true, message: 'Nhập nội dung thông báo'}]}>
          <Input.TextArea
            rows={3}
            placeholder="Ví dụ: Đã có phiên bản mới với nhiều cải tiến tốc độ và sửa lỗi. Vui lòng cập nhật..."
          />
        </Form.Item>

        <Form.Item
          label="Đường dẫn Store (CH Play / App Store)"
          name="storeUrl"
          rules={[{required: true, message: 'Nhập link tải app'}]}>
          <Input placeholder="https://..." />
        </Form.Item>

        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={isSaving}
          onClick={() => onSave(app, platform, form)}
          className="w-full mt-2 font-medium bg-blue-600">
          Lưu cấu hình {title}
        </Button>
      </Form>
    </Card>
  );
};

export const AppVersionSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const [formClientAndroid] = Form.useForm();
  const [formClientIos] = Form.useForm();
  const [formPartnerAndroid] = Form.useForm();
  const [formPartnerIos] = Form.useForm();

  const fetchVersions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await Api.get('/system-config/app-version/all');
      if (res?.data) {
        formClientAndroid.setFieldsValue(res.data.client?.android || {});
        formClientIos.setFieldsValue(res.data.client?.ios || {});
        formPartnerAndroid.setFieldsValue(res.data.partner?.android || {});
        formPartnerIos.setFieldsValue(res.data.partner?.ios || {});
      }
    } catch (error) {
      console.error('Lỗi tải cấu hình phiên bản:', error);
      message.error('Không thể tải cấu hình phiên bản ứng dụng');
    } finally {
      setLoading(false);
    }
  }, [formClientAndroid, formClientIos, formPartnerAndroid, formPartnerIos]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleSave = async (
    app: 'client' | 'partner',
    platform: 'android' | 'ios',
    form: any,
  ) => {
    const key = `${app}_${platform}`;
    try {
      setSavingKey(key);
      const values = await form.validateFields();
      await Api.put('/system-config/app-version', {
        app,
        platform,
        ...values,
      });
      message.success(
        `Cập nhật cấu hình ${app === 'client' ? 'Khách hàng' : 'Đối tác'} (${platform.toUpperCase()}) thành công!`,
      );
      fetchVersions();
    } catch (error: any) {
      console.error('Lỗi khi lưu cấu hình:', error);
      message.error(error?.data?.message || 'Có lỗi xảy ra khi lưu cấu hình');
    } finally {
      setSavingKey(null);
    }
  };

  const clientTabContent = (
    <Row gutter={[20, 20]}>
      <Col xs={24} lg={12}>
        <PlatformCard
          app="client"
          platform="android"
          form={formClientAndroid}
          title="Android (CH Play)"
          icon={<AndroidOutlined className="text-green-600 text-lg" />}
          isSaving={savingKey === 'client_android'}
          onSave={handleSave}
        />
      </Col>
      <Col xs={24} lg={12}>
        <PlatformCard
          app="client"
          platform="ios"
          form={formClientIos}
          title="iOS (App Store)"
          icon={<AppleOutlined className="text-gray-800 text-lg" />}
          isSaving={savingKey === 'client_ios'}
          onSave={handleSave}
        />
      </Col>
    </Row>
  );

  const partnerTabContent = (
    <Row gutter={[20, 20]}>
      <Col xs={24} lg={12}>
        <PlatformCard
          app="partner"
          platform="android"
          form={formPartnerAndroid}
          title="Android (CH Play)"
          icon={<AndroidOutlined className="text-green-600 text-lg" />}
          isSaving={savingKey === 'partner_android'}
          onSave={handleSave}
        />
      </Col>
      <Col xs={24} lg={12}>
        <PlatformCard
          app="partner"
          platform="ios"
          form={formPartnerIos}
          title="iOS (App Store)"
          icon={<AppleOutlined className="text-gray-800 text-lg" />}
          isSaving={savingKey === 'partner_ios'}
          onSave={handleSave}
        />
      </Col>
    </Row>
  );

  return (
    <Card
      className="shadow-sm border rounded-xl"
      title={
        <div className="flex items-center justify-between">
          <span className="font-bold text-lg text-gray-800 flex items-center gap-2">
            <RocketOutlined className="text-blue-600" /> Quản lý Phiên bản Ứng dụng & Cập nhật bắt buộc (Force Update)
          </span>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={fetchVersions}
            size="small">
            Làm mới
          </Button>
        </div>
      }>
      <p className="text-gray-500 text-sm mb-4">
        Khi bạn phát hành phiên bản mới sửa lỗi hoặc đổi API (như Goong Map), bạn có thể nâng version tối thiểu tại đây để buộc người dùng và tài xế nâng cấp app, ngăn chặn việc sử dụng bản cũ gây phát sinh chi phí hoặc lỗi.
      </p>

      <Tabs
        defaultActiveKey="client"
        type="card"
        items={[
          {
            key: 'client',
            label: (
              <span className="font-semibold px-2">
                📱 App Khách hàng (Client)
              </span>
            ),
            children: clientTabContent,
          },
          {
            key: 'partner',
            label: (
              <span className="font-semibold px-2">
                🛵 App Tài xế / Đối tác (Partner)
              </span>
            ),
            children: partnerTabContent,
          },
        ]}
      />
    </Card>
  );
};
